import type { TicketAttachmentView } from '@nadoutong/contracts';
import { Loader2, X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { getTicketAttachmentContent } from '../../api/tickets.ts';
import { useTicketAttachments } from '../../api/useTickets.ts';
import { Modal } from '../../components/Modal.tsx';
import { useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';

/**
 * Attachment thumbnails for an expanded ticket row (router #2070).
 *
 * Mount this only when the row is open and `attachmentCount > 0` so the
 * list never N+1s. Metadata goes through react-query; content bytes go
 * through the same credentialed fetch channel and become object URLs
 * that this component revokes on unmount / collapse. The originals are
 * ≤2 MB — CSS scales the thumbs; there is no server thumbnail.
 *
 * Waiting state is a single spinner + `tickets.attachments.loading`
 * label (desktop-aligned), not skeleton tiles.
 */
interface Props {
  readonly ticketId: string;
}

interface PreviewItem {
  readonly attachment: TicketAttachmentView;
  readonly url: string;
}

export function TicketAttachments({ ticketId }: Props): JSX.Element | null {
  const t = useT();
  const titleId = useId();
  const { fetchImpl } = useSession();
  const metaQ = useTicketAttachments(ticketId, true);
  const previews = useAttachmentPreviews(ticketId, metaQ.data?.attachments, fetchImpl);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected =
    selectedId === null
      ? undefined
      : previews.items.find((item) => item.attachment.id === selectedId);

  if (metaQ.isError || previews.status === 'error') {
    return (
      <p
        role="alert"
        className="m-0 text-caption text-bad"
        data-testid={`tickets-attachments-error-${ticketId}`}
      >
        {t('tickets.attachments.error')}
      </p>
    );
  }

  const waiting =
    metaQ.isLoading ||
    previews.status === 'loading' ||
    (metaQ.isSuccess && previews.status !== 'ready');

  if (waiting) {
    return (
      <div
        className="flex items-center gap-2 text-caption text-ink-low"
        data-testid={`tickets-attachments-loading-${ticketId}`}
      >
        <Loader2 aria-hidden="true" className="h-3.5 w-3.5 animate-spin" />
        {t('tickets.attachments.loading')}
      </div>
    );
  }

  if (previews.items.length === 0) {
    return null;
  }

  return (
    <>
      <ul
        className="m-0 flex list-none flex-wrap gap-2 p-0"
        data-testid={`tickets-attachments-${ticketId}`}
      >
        {previews.items.map((item) => (
          <li key={item.attachment.id}>
            <button
              type="button"
              data-testid={`tickets-attachment-${item.attachment.id}`}
              aria-label={item.attachment.filename}
              onClick={(event) => {
                event.stopPropagation();
                setSelectedId(item.attachment.id);
              }}
              className="block h-16 w-16 overflow-hidden rounded border border-line bg-well transition-colors duration-150 hover:border-line-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40"
            >
              <img
                src={item.url}
                alt={item.attachment.filename}
                className="h-full w-full object-cover"
              />
            </button>
          </li>
        ))}
      </ul>
      <Modal
        isOpen={selected !== undefined}
        onClose={() => setSelectedId(null)}
        titleId={titleId}
        testId="tickets-attachment-lightbox"
      >
        {selected === undefined ? null : (
          <div className="flex flex-col gap-3 p-5">
            <div className="flex items-start gap-3">
              <h2 id={titleId} className="m-0 min-w-0 flex-1 text-title text-ink">
                {t('tickets.attachments.lightbox.title')}
              </h2>
              <button
                type="button"
                aria-label={t('tickets.attachments.lightbox.close')}
                onClick={() => setSelectedId(null)}
                data-testid="tickets-attachment-lightbox-close"
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-ink-low transition-colors duration-150 hover:bg-active hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright focus-visible:ring-offset-2 focus-visible:ring-offset-card"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            </div>
            <img
              src={selected.url}
              alt={selected.attachment.filename}
              className="max-h-[70vh] w-auto max-w-full object-contain"
            />
          </div>
        )}
      </Modal>
    </>
  );
}

type PreviewStatus = 'idle' | 'loading' | 'error' | 'ready';

function useAttachmentPreviews(
  ticketId: string,
  attachments: readonly TicketAttachmentView[] | undefined,
  fetchImpl: typeof fetch,
): { readonly status: PreviewStatus; readonly items: readonly PreviewItem[] } {
  const [status, setStatus] = useState<PreviewStatus>('idle');
  const [items, setItems] = useState<readonly PreviewItem[]>([]);

  useEffect(() => {
    if (attachments === undefined) {
      setStatus('idle');
      setItems([]);
      return;
    }
    if (attachments.length === 0) {
      setStatus('ready');
      setItems([]);
      return;
    }

    let cancelled = false;
    const created: string[] = [];
    const abort = new AbortController();
    setStatus('loading');
    setItems([]);

    void (async () => {
      try {
        const blobs = await Promise.all(
          attachments.map(async (attachment) => {
            const blob = await getTicketAttachmentContent(
              ticketId,
              attachment.id,
              fetchImpl,
              abort.signal,
            );
            const typed =
              blob.type.length > 0 ? blob : new Blob([blob], { type: attachment.contentType });
            return { attachment, blob: typed };
          }),
        );
        if (cancelled) {
          return;
        }
        const loaded: PreviewItem[] = blobs.map((entry) => {
          const url = URL.createObjectURL(entry.blob);
          created.push(url);
          return { attachment: entry.attachment, url };
        });
        setItems(loaded);
        setStatus('ready');
      } catch {
        if (!cancelled) {
          setStatus('error');
          setItems([]);
        }
      }
    })();

    return () => {
      cancelled = true;
      abort.abort();
      for (const url of created) {
        URL.revokeObjectURL(url);
      }
    };
  }, [ticketId, fetchImpl, attachments]);

  return { status, items };
}
